import { EventEmitter } from "@/shared/eventEmiters/EventEmitter";
import "./Markets.css";
import { useEffect, useMemo, useRef } from "react";
import type { OnStateChange } from "@/components/mosaic/ui/TileTitle/TileTitle";
import { WidgetViewProvider } from "@/context/WidgetViewContext";
import { EVENTS } from "@/shared/eventEmiters/consts";
import { Monitor } from "../../Monitor/ui/Monitor";
import { News } from "../../News";
import { Sectors } from "../../Sectors";
import {
	benchmarkScreenerGridState,
	earningsNewsState,
	generalNewsState,
	newsHeightClass,
	sectorsGridState,
	transcriptsNewsState,
} from "../config";
import { EarningsCalendar } from "./components/EarningsCalendar";
import { MarketsFooter } from "./components/MarketsFooter";
import { MarketsHeader } from "./components/MarketsHeader";
import { ScrollToTopButton } from "./components/ScrollToTopButton";
import { StockScanners } from "./components/StockScanners";

export const Markets = ({
	tileId,
	onStateChange,
}: {
	tileId: string;
	onStateChange?: OnStateChange;
}) => {
	const scrollRef = useRef<HTMLDivElement | null>(null);

	const benchmarkEventEmitter = useMemo(() => new EventEmitter(), []);
	const stockScannersEventEmitter = useMemo(() => new EventEmitter(), []);

	useEffect(() => {
		benchmarkEventEmitter.emit(EVENTS.CUSTOM_SORT_ENABLED, undefined);
	}, [benchmarkEventEmitter]);

	return (
		<div
			id={tileId}
			className="h-full flex flex-col relative"
			data-testid="markets-component"
		>
			<div className="sticky top-0 z-10 bg-background">
				<MarketsHeader eventEmitter={stockScannersEventEmitter} />
			</div>

			<div
				className="flex-1 overflow-y-auto flex flex-col gap-4"
				ref={scrollRef}
			>
				<ScrollToTopButton containerRef={scrollRef} />

				<div className="m-1 mt-2" id="macro">
					<h2 className="text-accent-foreground text-lg text-center mb-3">
						Benchmarks
					</h2>
					<div className="h-[320px]">
						<WidgetViewProvider>
							<Monitor
								state={benchmarkScreenerGridState}
								eventEmitter={benchmarkEventEmitter}
								tileId={`${tileId}-benchmark-grid`}
								onStateChange={(state) => {
									if (state?.ticker) {
										onStateChange?.({ ticker: state.ticker });
									}
								}}
							/>
						</WidgetViewProvider>
					</div>
				</div>
				<div data-markets-news className="m-1 h-full">
					<News
						state={generalNewsState}
						eventEmitter={new EventEmitter()}
						tileId={`${tileId}-general-news-grid`}
						header={
							<h2 className="text-accent-foreground text-lg text-center mb-3">
								Market News
							</h2>
						}
						className={newsHeightClass}
					/>
				</div>
				<div className="m-1" id="sectors">
					<h2 className="text-accent-foreground text-lg text-center mb-3">
						Sectors
					</h2>
					<div className="h-[222px]">
						<WidgetViewProvider>
							<Monitor
								state={sectorsGridState}
								eventEmitter={new EventEmitter()}
								tileId={`${tileId}-sectors-grid`}
								onStateChange={(state) => {
									if (state?.ticker) {
										onStateChange?.({ ticker: state.ticker });
									}
								}}
							/>
						</WidgetViewProvider>
					</div>
				</div>
				<div className="m-1">
					<Sectors />
				</div>
				<div className="m-1 @container" id="stock-scanners">
					<h2 className="text-accent-foreground text-lg text-center mt-2 mb-5">
						Stock Scanners
					</h2>
					<StockScanners
						onStateChange={onStateChange}
						eventEmitter={stockScannersEventEmitter}
					/>
				</div>
				<div data-markets-news id="earnings" className="m-1 h-full">
					<News
						state={earningsNewsState}
						eventEmitter={new EventEmitter()}
						tileId={`${tileId}-earnings-news-grid`}
						header={
							<h2 className="text-accent-foreground text-lg text-center mb-3">
								Earnings News
							</h2>
						}
						className={newsHeightClass}
					/>
				</div>
				<div className="m-1" id="calendar" data-markets-earnings-calendar>
					<h2 className="text-accent-foreground text-lg text-center mb-3">
						Earnings Calendar
					</h2>
					<EarningsCalendar onStateChange={onStateChange} />
				</div>
				<div data-markets-news id="transcripts" className="m-1 h-full">
					<News
						state={transcriptsNewsState}
						eventEmitter={new EventEmitter()}
						tileId={`${tileId}-transcripts-news-grid`}
						header={
							<h2 className="text-accent-foreground text-lg text-center mb-3">
								Transcripts
							</h2>
						}
						className={newsHeightClass}
					/>
				</div>

				<MarketsFooter />
			</div>
		</div>
	);
};
